import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  canvasApi,
  type CanvasWithUrls,
  type CreateCanvasInput,
  type UpdateCanvasMetaInput,
} from '../../../services/canvas';
import { useGamificationStore } from '../../../stores/gamification-store.js';
import { XP_RULES, type CanvasResponse } from '@xdface-vault/shared';

const canvasKeys = {
  all: ['canvases'] as const,
  lists: () => [...canvasKeys.all, 'list'] as const,
  details: () => [...canvasKeys.all, 'detail'] as const,
  detail: (id: string) => [...canvasKeys.details(), id] as const,
};

// List all canvases for the current user
export function useCanvasList() {
  return useQuery({
    queryKey: canvasKeys.lists(),
    queryFn: () => canvasApi.listCanvases(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get a single canvas (metadata + presigned URLs)
export function useCanvas(id: string | undefined) {
  return useQuery({
    queryKey: canvasKeys.detail(id!),
    queryFn: () => canvasApi.getCanvas(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// Create a new canvas with optimistic update
export function useCreateCanvas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCanvasInput) => canvasApi.createCanvas(input),
    onMutate: async (newCanvas) => {
      await queryClient.cancelQueries({ queryKey: canvasKeys.lists() });
      const previousData = queryClient.getQueryData<CanvasResponse[]>(canvasKeys.lists());

      const optimistic: CanvasWithUrls = {
        dataUrl: '',
        uploadUrl: '',
        id: `optimistic-${Date.now()}`,
        userId: 'me',
        title: newCanvas.title,
        tags: newCanvas.tags ?? [],
        s3Key: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 1,
      };

      queryClient.setQueryData<CanvasResponse[]>(canvasKeys.lists(), (old) => [
        optimistic,
        ...(old ?? []),
      ]);

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(canvasKeys.lists(), context.previousData);
      }
    },
    onSuccess: () => {
      useGamificationStore.getState().addXP(XP_RULES.CANVAS_CREATED);
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: canvasKeys.lists() });
    },
  });
}

// Save canvas data (PUT to presigned S3 URL)
export function useSaveCanvas() {
  return useMutation({
    mutationFn: ({ uploadUrl, data }: { uploadUrl: string; data: object }) =>
      canvasApi.saveCanvasData(uploadUrl, data),
  });
}

// Update canvas metadata
export function useUpdateCanvasMeta() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCanvasMetaInput }) =>
      canvasApi.updateCanvasMeta(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: canvasKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: canvasKeys.lists() });
    },
  });
}

// Delete canvas with optimistic update
export function useDeleteCanvas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => canvasApi.deleteCanvas(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: canvasKeys.lists() });
      const previousData = queryClient.getQueryData<CanvasResponse[]>(canvasKeys.lists());

      queryClient.setQueryData<CanvasResponse[]>(canvasKeys.lists(), (old) =>
        (old ?? []).filter((c) => c.id !== id),
      );

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(canvasKeys.lists(), context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: canvasKeys.lists() });
    },
  });
}

// Share canvas
export function useShareCanvas() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ttlMinutes }: { id: string; ttlMinutes: 15 | 30 | 60 | 90 | 120 }) =>
      canvasApi.shareCanvas(id, ttlMinutes),
    onSuccess: (_data, { id }) => {
      useGamificationStore.getState().addXP(XP_RULES.SHARING_UNLOCKED);
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
      queryClient.invalidateQueries({ queryKey: canvasKeys.detail(id) });
    },
  });
}

// Revoke canvas share
export function useDeleteCanvasShare() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => canvasApi.deleteCanvasShare(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: canvasKeys.detail(id) });
    },
  });
}
